import { useContext, useEffect, useState } from 'react';
import { formatPrice, type AnalysisResult, type Squad, type SquadPlayer, type SquadValidation } from '@fantasy/types';
import { PublicTryActionsContext } from '../App.tsx';
import { api, ApiRequestError, type PublicPlayerAssessment } from '../api.ts';
import { AnalysisView } from './AnalysisView.tsx';

/** Same order `SquadView`'s own pitch uses. */
const POSITION_ORDER = ['GKP', 'DEF', 'MID', 'FWD'] as const;

interface PublicVerdictProps {
  entryId: number;
  /** The gameweek a share link (T-089) points at — omitted for the plain
   * `/try/:entryId` front door, where the entry's current gameweek is
   * exactly what a visitor wants. Passed straight through to both fetches
   * below, so a gameweek 6 link stays gameweek 6 even if it's opened once
   * gameweek 9 is live. */
  gameweek?: number;
}

type SquadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; squad: Squad; validation: SquadValidation; assessments: PublicPlayerAssessment[] };

/** A signed-out visitor's squad request failing "for FPL reasons" (a bad id,
 * a private team, FPL itself being down) is already worded for a human by
 * the server — see `UNAVAILABLE_REASON` in `publicSquad.ts` — so this only
 * needs to intercept the one code that isn't: a shared, IP-keyed rate limit
 * reading "Too many requests" reads as the visitor's own fault unless it's
 * rewritten here. */
function describeSquadError(err: unknown): string {
  if (err instanceof ApiRequestError) {
    if (err.code === 'RATE_LIMITED') {
      return "This free tool is getting a lot of use right now — that's shared across every visitor, not something you did. Try again in a few minutes.";
    }
    return err.message;
  }
  return 'Something went wrong loading this team. Please try again.';
}

/** Same reasoning as `describeSquadError`, worded for the verdict's own
 * (tighter) limit specifically, since that's the number a retry actually
 * has to wait out. */
function describeVerdictError(err: unknown): string {
  if (err instanceof ApiRequestError) {
    if (err.code === 'RATE_LIMITED') {
      return "The free verdict is capped at a few requests an hour across every visitor, and that's been reached for now — nothing wrong with this request. Try again shortly.";
    }
    return err.message;
  }
  return 'Something went wrong getting the verdict. Please try again.';
}

/**
 * The `/try` route's content (T-088): a public FPL entry id resolved to its
 * squad and scored assessment — no account, nothing written server-side —
 * shown in full before the verdict is ever asked for. Requesting the
 * verdict is a second, deliberate step with its own honest cost framing;
 * only once one exists does the sign-up pitch appear, and it names what an
 * account adds rather than just asking.
 */
export function PublicVerdict({ entryId, gameweek }: PublicVerdictProps) {
  const [state, setState] = useState<SquadState>({ status: 'loading' });
  const [verdict, setVerdict] = useState<AnalysisResult | null>(null);
  const [verdictError, setVerdictError] = useState<string | null>(null);
  const [requestingVerdict, setRequestingVerdict] = useState(false);
  const gateActions = useContext(PublicTryActionsContext);

  // A fresh id (a manager looking up someone else, or their own after this
  // one) starts the whole page over — a stale squad or verdict from the
  // last id must never linger under this one. A change in `gameweek` alone
  // (the same entry, a different shared link) needs exactly the same reset:
  // gameweek 6's squad and verdict must never linger under gameweek 9's
  // headline.
  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    setVerdict(null);
    setVerdictError(null);

    api
      .getPublicSquad(entryId, gameweek)
      .then(({ squad, validation, assessments }) => {
        if (!cancelled) setState({ status: 'ready', squad, validation, assessments });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ status: 'error', message: describeSquadError(err) });
      });

    return () => {
      cancelled = true;
    };
  }, [entryId, gameweek]);

  async function onRequestVerdict() {
    setRequestingVerdict(true);
    setVerdictError(null);
    try {
      const { verdict: result } = await api.getPublicVerdict(entryId, {}, gameweek);
      setVerdict(result);
    } catch (err) {
      setVerdictError(describeVerdictError(err));
    } finally {
      setRequestingVerdict(false);
    }
  }

  if (state.status === 'loading') {
    return (
      <section className="panel rise-in" aria-live="polite">
        <p className="hint">Loading this squad from FPL…</p>
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      // h2, not h3 (T-100): this replaces the "ready" state's own h2 as the
      // first heading under `/try`'s masthead h1 — an h3 here skipped a
      // level.
      <section className="panel panel--bad rise-in" aria-live="polite">
        <h2>Couldn&rsquo;t load that team</h2>
        <p>{state.message}</p>
      </section>
    );
  }

  const { squad, validation, assessments } = state;
  const assessmentByName = new Map(assessments.map((a) => [a.name, a]));
  const ranked = [...assessments].sort((a, b) => b.score - a.score);
  const bench = squad.players
    .filter((player) => !player.isStarting)
    .sort((a, b) => (a.benchOrder ?? 0) - (b.benchOrder ?? 0));

  return (
    <>
      <section className="panel panel--pitch rise-in" aria-labelledby="public-squad-heading">
        <div className="panel-head">
          <div>
            <h2 id="public-squad-heading">{squad.name}</h2>
            <p className="hint">Gameweek {squad.gameweek} · free preview, no account needed</p>
          </div>
        </div>

        {!validation.valid && (
          <section className="panel panel--bad mt-md" aria-label="Squad rule violations">
            <h3>This squad isn&rsquo;t legal right now</h3>
            <ul className="notices">
              {validation.violations.map((violation) => (
                <li key={violation.message} className="error">
                  {violation.message}
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="pitch mt-xl">
          {POSITION_ORDER.map((position) => {
            const players = squad.players.filter((player) => player.isStarting && player.position === position);
            if (players.length === 0) return null;

            return (
              <div key={position} className="pitch-row">
                {players.map((player) => (
                  <PublicPlayerCard key={player.name} player={player} score={assessmentByName.get(player.name)?.score} />
                ))}
              </div>
            );
          })}
        </div>

        <div className="dugout">
          <p className="dugout-label">Bench</p>
          <div className="dugout-row">
            {bench.map((player) => (
              <PublicPlayerCard key={player.name} player={player} score={assessmentByName.get(player.name)?.score} />
            ))}
          </div>
        </div>

        <div className="scroll-x mt-lg">
          <table>
            <caption className="hint table-caption">
              Every player, scored — free, no verdict needed
            </caption>
            <thead>
              <tr>
                <th scope="col">Player</th>
                <th scope="col">Pos</th>
                <th scope="col">Club</th>
                <th scope="col" className="num">
                  Price
                </th>
                <th scope="col" className="num">
                  Form
                </th>
                <th scope="col" className="num">
                  Fixtures
                </th>
                <th scope="col" className="num">
                  Score
                </th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((assessment) => (
                <tr key={assessment.name}>
                  <td>
                    {assessment.name}
                    {assessment.flags.map((flag) => (
                      <span key={flag} className="pill">
                        {flag.replace('_', ' ')}
                      </span>
                    ))}
                    {assessment.news && <div className="callout-detail">{assessment.news}</div>}
                  </td>
                  <td>{assessment.position}</td>
                  <td>{assessment.club}</td>
                  <td className="num">{formatPrice(assessment.price)}</td>
                  <td className="num">{assessment.form.toFixed(1)}</td>
                  <td className="num">{assessment.fixtureOutlook.toFixed(1)}</td>
                  <td className="num">{assessment.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel panel--accent-2 rise-in" aria-labelledby="public-verdict-heading">
        <h3 id="public-verdict-heading">Want the verdict?</h3>
        <p className="hint">
          Everything above is deterministic arithmetic — free, instant, and exactly reproducible.
          The verdict is different: a real model call that reads this squad and argues for a
          captain, a transfer and a chip call. That&rsquo;s why it takes a moment, and it&rsquo;s
          the reason accounts exist on this site at all — not to gate this read, but to save the
          good ones.
        </p>
        <button type="button" onClick={onRequestVerdict} disabled={requestingVerdict} aria-busy={requestingVerdict}>
          {requestingVerdict && <span className="spinner" aria-hidden="true" />}
          {requestingVerdict ? 'Thinking…' : 'Get the verdict'}
        </button>
        {verdictError && (
          <ul className="notices mt-md">
            <li className="error">{verdictError}</li>
          </ul>
        )}
      </section>

      {verdict && <AnalysisView analysis={verdict} />}

      {verdict && (
        <section className="panel panel--accent rise-in" aria-labelledby="public-signup-heading">
          <h3 id="public-signup-heading">Keep this squad</h3>
          <p>
            An account saves this squad, sends a weekly briefing before your deadline, and tracks
            whether this verdict was actually right — none of which this page, on its own, can do.
          </p>
          {gateActions && (
            <button type="button" onClick={gateActions.onGetStarted}>
              Create a free account
            </button>
          )}
        </section>
      )}
    </>
  );
}

function PublicPlayerCard({ player, score }: { player: SquadPlayer; score: number | undefined }) {
  return (
    <div className="pitch-card" data-club={player.club}>
      <div className="name">
        {player.name}
        {player.isCaptain && <span className="pill pill--captain">C</span>}
        {player.isViceCaptain && <span className="pill pill--vice">V</span>}
        {score !== undefined && <span className="pill pill--good">{score}</span>}
      </div>
      <div className="meta">
        {player.club} · {formatPrice(player.price)}
      </div>
    </div>
  );
}
